import { useAuthStore } from '~/stores/authStore'

export default defineNuxtRouteMiddleware(async (to, from) => {
    const authStore = useAuthStore()

    // Skip middleware on server-side if needed, or handle via cookies
    // For now, we rely on client-side state for simple protection
    if (import.meta.server) return

    // Immediately handle signout securely before hydrating layout
    if (to.path === '/logout') {
        const userCookie = useCookie('user')
        if (userCookie.value) userCookie.value = null

        authStore.clearUser()

        // Let Firebase clear session without blocking routing
        if (import.meta.client) {
            import('firebase/auth').then(({ getAuth, signOut }) => {
                const auth = getAuth()
                if (auth) signOut(auth).catch(() => { })
            })
        }

        return navigateTo('/login')
    }

    // /register is the canonical signup path (replaces the older
    // /signup + /setup-account two-step flow). It's public — users
    // hitting it shouldn't be required to be authed.
    const isPublicPage = to.path === '/login' || to.path === '/register' || to.meta.unauthenticatedOnly === true
    // Debug-auth is exempt from the claim gate so we can SEE the
    // missing-claim state when diagnosing onboarding issues. Authed
    // user, no claim, want to look at what's wrong → /debug-auth.
    const isDebugAuthPage = to.path === '/debug-auth'
    const requiresAuth = to.meta.authenticated !== false && !isPublicPage

    // 💬 Auth-hydration race — wait for Firebase to finish.
    //
    // On a hard refresh the user cookie is restored synchronously
    // (so `isLoggedIn` is true), but `onAuthStateChanged` fires
    // asynchronously — until it does, `accountId` is null. Without
    // waiting, we'd see "logged in but no accountId" and bounce
    // every protected route to /register, which destroys the URL
    // the user actually wanted.
    //
    // Solution: when the cookie says "I was logged in," await the
    // plugin's hydration promise before evaluating the accountId
    // gate. The promise resolves on the FIRST onAuthStateChanged
    // callback, which is also when accountId gets populated.
    // No cookie → user wasn't logged in → no race to wait on.
    const userCookie = useCookie('user')
    if (userCookie.value && !isPublicPage) {
        const { $authHydrated } = useNuxtApp() as { $authHydrated?: Promise<void> }
        if ($authHydrated) {
            // Belt-and-suspenders timeout: if Firebase never replies
            // (offline first paint, plugin failed to load, etc.) we
            // shouldn't hang the navigation forever. 3s is generous —
            // a healthy onAuthStateChanged fires within ~50ms.
            await Promise.race([
                $authHydrated,
                new Promise<void>((resolve) => setTimeout(resolve, 3000)),
            ])
        }
    }

    // If page requires auth and user is not logged in, redirect to login
    if (requiresAuth && !authStore.isLoggedIn) {
        return navigateTo({
            path: '/login',
            query: { redirect: to.fullPath }
        })
    }

    // If user is logged in and tries to access public only pages (login/signup)
    //
    // Honor a `?redirect=…` round-trip target if present. The unauthed
    // case above redirected here with `query: { redirect: to.fullPath }`,
    // so a user who hit a protected URL → got bounced to /login → signed
    // in lands BACK on the original URL instead of `/`. Critical for
    // deep-link flows like /integrations/intuit?realmId=… where Intuit
    // sends the user to a specific page and we'd lose the realmId if we
    // dropped them on the homepage.
    //
    // Defense: only honor same-origin relative paths (must start with `/`
    // but not `//`) — prevents an open-redirect via crafted /login?redirect=
    // links to off-site domains.
    if (isPublicPage && authStore.isLoggedIn) {
        const raw = (to.query?.redirect ?? '') as string
        const safeRedirect =
            typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')
                ? raw
                : '/'
        return navigateTo(safeRedirect)
    }

    // 💬 Account provisioning gate.
    // An authed user whose JWT doesn't carry an `accountId` claim hasn't
    // finished signup — bounce them to /register. In normal operation
    // every signup goes through /register and lands a claim atomically,
    // so this is a defensive net for a corrupted state (auth user
    // exists but account doc / claim is missing) — typically caused by
    // a partial signup that hit an error after creating the auth user
    // but before writing claims. The user re-runs /register; the
    // duplicate-email check there returns a clear error.
    if (authStore.isLoggedIn && !isPublicPage && !isDebugAuthPage) {
        const globalsStore = useGlobalsStore()
        const { accountId, accountProfile } = storeToRefs(globalsStore)
        if (!accountId.value) {
            return navigateTo({
                path: '/register',
                query: { redirect: to.fullPath },
            })
        }

        // 💬 Setup wizard gate.
        //
        // A signed-in user whose Snowflake setup isn't complete is
        // routed to /setup. The wizard provisions the system role +
        // system user + engine install — without those, most of the
        // app can't actually do anything (no engine to run workflows,
        // no system user to authenticate scheduled jobs, etc.).
        //
        // Exempt:
        //   - /setup itself (obviously)
        //   - /logout (so they can always sign out)
        //   - /oauth (the OAuth callback receiver — runs without
        //     a full setup, finishes its handshake, redirects)
        //
        // We treat setup as complete when engineInstall.status === 'complete'.
        // Stages 1-3 not being complete just means the user hasn't
        // gotten that far — the wizard surfaces what's needed.
        //
        // Defensive: if accountProfile hasn't hydrated yet (subscription
        // not fired), don't bounce — let the user pass through; the
        // sub will fire and the next navigation gates correctly. Avoids
        // an infinite redirect on a slow first paint.
        const isSetupPage = to.path === '/setup'
        const isLogoutPage = to.path === '/logout'
        const isOAuthCallback = to.path === '/oauth'
        const profile: any = accountProfile.value
        const setupProgress = profile?.setupProgress
        const profileHydrated = !!profile && Object.keys(profile).length > 0
        const setupComplete = setupProgress?.engineInstall?.status === 'complete'

        if (
            profileHydrated
            && !setupComplete
            && !isSetupPage
            && !isLogoutPage
            && !isOAuthCallback
        ) {
            return navigateTo('/setup')
        }
    }
})
